import { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';

/**
 * Mail & agenda-toegang (Frits + Overdracht): welke mailboxen mag de app in?
 * Lidmaatschap zit in de Exchange-distributiegroep FF-MailAssistent-Toegang en
 * is alleen via Exchange PowerShell te wijzigen — dit scherm toont de leden
 * (als het token dat mag lezen) en zet het commando kant-en-klaar klaar.
 * De "Geen toegang tot de agenda van …"-fout in de overdrachtstool wijst hierheen.
 */
function MailToegang() {
  const [data, setData] = useState(null);
  const [mailbox, setMailbox] = useState('');

  useEffect(() => {
    authFetch(`${BACKEND_URL}/Api/admin/mail-toegang`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d || { groep: 'FF-MailAssistent-Toegang', leden: null }))
      .catch(() => setData({ groep: 'FF-MailAssistent-Toegang', leden: null }));
  }, []);

  const cmd = `Add-DistributionGroupMember -Identity ${data?.groep || 'FF-MailAssistent-Toegang'} -Member ${mailbox.trim() || '<mailbox>@fullframe.nl'}`;

  if (!data) return <CircularProgress size={20} />;
  return (
    <Box sx={{ maxWidth: 760 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Krijgt iemand in de overdrachtstool <b>"Geen toegang tot de agenda van …"</b>, dan zit die
        mailbox nog niet in de groep <b>{data.groep}</b>. Toevoegen kan alleen via Exchange Online
        PowerShell (het is een distributiegroep) — vul hieronder het adres in en kopieer het commando.
        Na uitvoeren kan het tot ±30 minuten duren voordat het doorwerkt.
      </Alert>

      {Array.isArray(data.leden) ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>
            HUIDIGE LEDEN ({data.leden.length})
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {data.leden.map((l) => <Chip key={l.mail} size="small" label={l.mail} title={l.naam || ''} />)}
          </Stack>
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          De ledenlijst is met de huidige app-rechten niet uit te lezen — bekijk hem in Exchange
          admin of met <code>Get-DistributionGroupMember -Identity {data.groep}</code>.
        </Typography>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
        <TextField size="small" label="Mailbox toevoegen" placeholder="naomi@fullframe.nl"
          value={mailbox} onChange={(e) => setMailbox(e.target.value)} sx={{ minWidth: 260 }} />
        <Button variant="contained" disabled={!mailbox.trim()}
          onClick={async () => {
            try { await navigator.clipboard.writeText(cmd); toast.success('PowerShell-commando gekopieerd'); }
            catch { toast.error('Kopiëren geblokkeerd — selecteer het commando handmatig'); }
          }}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Kopieer PowerShell-commando
        </Button>
      </Stack>
      <Box component="pre" sx={{ p: 1.5, bgcolor: '#0f172a', color: '#e2e8f0', borderRadius: 1.5, fontSize: 13, overflowX: 'auto' }}>
        {`Connect-ExchangeOnline\n${cmd}`}
      </Box>
    </Box>
  );
}

export default MailToegang;
